import { useDataTableApi } from "@/hooks/useDataTableApi";
import { membersTableConfig } from "@/config/members-table.config";
import {
  membersService,
  Member,
  exitFamilyAsMember,
} from "@/services/members.service";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedMember } from "@/store/authSlice";
import { setCredentials } from "@/store/authSlice";

// Fetch all members for the table
import {
  DerivedMemberType,
  isChild,
  MemberInfo,
  MemberType,
  UpdateMemberContactRequest,
} from "@/types/member";

// Removed getAllMembers: use family-scoped fetch instead, or implement as needed.

export function parseDob(dobStr: string) {
  if (!dobStr)
    return { dob_day: undefined, dob_month: undefined, dob_year: undefined };
  const [year, month, day] = dobStr.split("-");
  return {
    dob_day: day ? Number(day) : undefined,
    dob_month: month ? Number(month) : undefined,
    dob_year: year ? Number(year) : undefined,
  };
}

// Converts { dob_day, dob_month, dob_year } to "YYYY-MM-DD" string
export function dobPartsToString(
  dob_day?: number,
  dob_month?: number,
  dob_year?: number
): string {
  if (!dob_day || !dob_month || !dob_year) return "";
  const mm = dob_month.toString().padStart(2, "0");
  const dd = dob_day.toString().padStart(2, "0");
  return `${dob_year}-${mm}-${dd}`;
}

export function getMemberInfo(ignoreSelectedMember = false): MemberInfo {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- legacy custom hook called as getMemberInfo() by 112 call sites; rename deferred to follow-up refactor
  const member = useSelector((state: RootState) => state.auth?.member);
  // eslint-disable-next-line react-hooks/rules-of-hooks -- legacy custom hook called as getMemberInfo() by 112 call sites; rename deferred to follow-up refactor
  const selectedMember = useSelector(
    (state: RootState) => state.auth?.selectedMember
  );
  const effectiveMember = ignoreSelectedMember
    ? member
    : selectedMember || member;
  const onBehalf =
    !ignoreSelectedMember &&
    !!selectedMember &&
    selectedMember.id !== member?.id;
  const derivedMemberType =
    effectiveMember?.member_type === MemberType.PET
      ? DerivedMemberType.P
      : isChild(effectiveMember)
      ? DerivedMemberType.C
      : DerivedMemberType.A;
  // Normalize otherAttrs/other_attrs for pet fields
  let otherAttrs = effectiveMember?.other_attrs;
  if (!otherAttrs) {
    otherAttrs = { docUploaded: false };
  }
  // Use other_attrs.isExperienced if present, else default to false
  const isExperienced =
    typeof otherAttrs.isExperienced === "boolean"
      ? otherAttrs.isExperienced
      : false;

  const memberInfo = {
    id: effectiveMember?.id,
    firstName: effectiveMember?.first_name,
    middleName: effectiveMember?.middle_name,
    lastName: effectiveMember?.last_name,
    dob: effectiveMember?.dob,
    dobYear: effectiveMember?.dob_year,
    dobMonth: effectiveMember?.dob_month,
    dobDay: effectiveMember?.dob_day,
    email: effectiveMember?.email,
    dialCode: effectiveMember?.dialCode,
    mobile: effectiveMember?.mobile,
    status: effectiveMember?.status,
    lastLogin: effectiveMember?.lastLogin,
    createdAt: effectiveMember?.createdAt || effectiveMember?.created_at, // Handle both camelCase and snake_case
    subExpired: effectiveMember?.subExpired,
    gender:
      typeof effectiveMember?.gender === "number"
        ? effectiveMember.gender
        : undefined,
    memberType: effectiveMember?.member_type, // Expose member_type for navigation and profile logic
    derivedMemberType,
    current_family: effectiveMember?.current_family,
    family_member_type: effectiveMember?.family_member_type,
    other_attrs: otherAttrs,
    avatar: effectiveMember?.avatar,
    nickname: effectiveMember?.nickname,
    emergencyCardReady: effectiveMember?.emergencyCardReady,
    registered_member: effectiveMember?.registered_member || false,
    registration_status: effectiveMember?.registration_status || "",
    isSuspended: effectiveMember?.isSuspended || false,
    lastAccessDate: effectiveMember?.lastAccessDate || "",
    role_id: effectiveMember?.role_id || "",
    onBehalf,
    isExperienced,
  };

  return memberInfo;
}

export function useProfileUpdate() {
  const dispatch = useAppDispatch();
  const { updateMember } = useMembers();

  async function handleProfileUpdate({
    memberId,
    updateData,
    isChanged,
    onSavingChange,
  }: {
    memberId: string;
    updateData: any;
    isChanged: boolean;
    onSavingChange?: (saving: boolean) => void;
  }) {
    if (!memberId || !isChanged) return { success: false };
    if (onSavingChange) onSavingChange(true);
    try {
      const updatedMember = await updateMember(memberId, updateData);
      // Preserve the role object if missing or downgraded to string
      updateLocalStorage(updatedMember, memberId);
      return { success: true, updatedMember };
    } catch (err: any) {
      return { success: false, error: err };
    } finally {
      if (onSavingChange) onSavingChange(false);
    }
  }

  const doUpdateMember = (updatedMember: Member, onBehalf = false) => {
    if (onBehalf) {
      localStorage.setItem("selectedMember", JSON.stringify(updatedMember));
      dispatch(setSelectedMember(updatedMember));
    } else {
      // SECURITY: Only update if the received member ID matches logged-in member ID
      const currentMember = JSON.parse(localStorage.getItem("member") || "{}");

      if (
        currentMember.id &&
        updatedMember.id &&
        currentMember.id !== updatedMember.id
      ) {
        console.warn(
          "[doUpdateMember] Member ID mismatch - preventing member swap",
          {
            currentId: currentMember.id,
            updatedId: updatedMember.id,
          }
        );
        return;
      }

      localStorage.setItem("member", JSON.stringify(updatedMember));
      dispatch(
        setCredentials({
          token: localStorage.getItem("token"),
          member: updatedMember,
        })
      );
    }
  };

  const handleContactUpdate = async ({
    memberId,
    updateData,
    isChanged,
    onSavingChange,
  }: {
    memberId: string;
    updateData: UpdateMemberContactRequest;
    isChanged: boolean;
    onSavingChange?: (saving: boolean) => void;
  }) => {
    if (!memberId || !isChanged) return { success: false };
    if (onSavingChange) onSavingChange(true);
    try {
      const updatedMember = await membersService.updateMemberContact(
        memberId,
        updateData
      );
      updateLocalStorage(updatedMember, memberId);
      return { success: true, updatedMember };
    } catch (err: any) {
      console.error("Error updating member contact:", err);
      return { success: false, error: err };
    } finally {
      if (onSavingChange) onSavingChange(false);
    }
  };

  // create a new function to get and update the member in local storage for the passed
  // member id
  const updateMemberInLocalStorage = async (
    memberId: string,
    onBehalf = false
  ) => {
    const member = await membersService.getMember(memberId);
    member.id = memberId;
    doUpdateMember(member, onBehalf);
    return member;
  };

  return {
    handleProfileUpdate,
    handleContactUpdate,
    updateMemberInLocalStorage,
  };

  function updateLocalStorage(updatedMember: Member, memberId: string) {
    const prevMember = JSON.parse(localStorage.getItem("member") || "{}");
    if (!updatedMember.role || typeof updatedMember.role === "string") {
      updatedMember.role = prevMember.role;
    }
    // Preserve family_member_type if missing in update response
    if (
      !("family_member_type" in updatedMember) &&
      "family_member_type" in prevMember
    ) {
      updatedMember.family_member_type = prevMember.family_member_type;
    }
    // Ensure roleType is present
    if (!updatedMember.roleType && prevMember.roleType) {
      updatedMember.roleType = prevMember.roleType;
    }
    // Update localStorage and Redux for logged-in member
    const stateMember = JSON.parse(localStorage.getItem("member") || "{}");
    if (memberId === stateMember.id) {
      doUpdateMember(updatedMember);
    }
    // Also update selectedMember if it matches
    const selectedMember = JSON.parse(
      localStorage.getItem("selectedMember") || "{}"
    );
    if (selectedMember && selectedMember.id === memberId) {
      localStorage.setItem("selectedMember", JSON.stringify(updatedMember));
      dispatch(setSelectedMember(updatedMember));
    }
  }
}

export const useMembers = (options?: { autoFetch?: boolean }) => {
  const autoFetch = options?.autoFetch !== false;
  const {
    data,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
  } = useDataTableApi({
    filterConfig: membersTableConfig.filters,
    fetchData: async () => ({ data: [], total: 0 }), // No-op: do not call /api/members/getAll
  });

  // Get logged-in member id
  const { id: loggedInMemberId } = getMemberInfo();

  // Add computed displayLabel for each member
  const membersWithLabel = (data || []).map((member) => ({
    ...member,
    displayLabel:
      member.id === loggedInMemberId
        ? "You"
        : `${member.first_name} ${member.last_name}`,
    displayFirstNameOrYou:
      member.id === loggedInMemberId ? "You" : member.first_name,
  }));

  // Single member actions
  const getMember = async (id: string) => {
    return membersService.getMember(id);
  };
  const createMember = async (data: Partial<Member>) => {
    return membersService.createMember(data);
  };
  const updateMember = async (id: string, data: Partial<Member>) => {
    return membersService.updateMember(id, data);
  };
  const deleteMember = async (id: string) => {
    return membersService.deleteMember(id);
  };
  // Remove member from family (exit)
  // Directly use imported function to avoid dynamic import issues
  // Types should be compatible with ExitFamilyDto

  // Fetch managed members directly
  const getManagedMembers = async (): Promise<Member[]> => {
    return membersService.getManagedMembers();
  };

  // Get dispatch at the hook level
  const dispatch = useAppDispatch();

  // Update the selected member in Redux and localStorage
  const updateSelectedMember = (updatedMember: Partial<Member>) => {
    return (memberId: string) => {
      try {
        // Get current selected member from Redux
        const selectedMember = JSON.parse(
          localStorage.getItem("selectedMember") || "{}"
        );

        // If the updated member is the currently selected member, update it
        if (selectedMember && selectedMember.id === memberId) {
          const updatedSelectedMember = { ...selectedMember, ...updatedMember };

          // Update localStorage
          localStorage.setItem(
            "selectedMember",
            JSON.stringify(updatedSelectedMember)
          );

          // Update Redux store
          dispatch(setSelectedMember(updatedSelectedMember));

          return true;
        }
        return false;
      } catch (error) {
        console.error("Error updating selected member:", error);
        return false;
      }
    };
  };

  return {
    data: [], // No global members list, only use family-specific data elsewhere
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
    getMember,
    createMember,
    updateMember,
    deleteMember,
    getManagedMembers,
    exitFamilyAsMember,
    updateSelectedMember, // Export the new method
  };
};
